import { Injectable } from "@angular/core";
import { PartRepository } from "@partikelart/parts/data-access-core";
import type { CreatePartInput, Part } from "@partikelart/parts/domain";
import { Observable, of, throwError } from "rxjs";

/** Default mock parts used to seed the in-memory store. */
export const DEFAULT_PARTS: Part[] = [
	{
		id: "00000000-0000-4000-8000-000000000001",
		partNumber: "ART-001",
		partName: "Scheibe",
		siteId: "00000000-0000-4000-8000-000000000010",
		imageBlobPath: "part/11111111-0000-4000-8000-000000000001",
		active: true,
		createdAt: new Date("2025-01-10T09:00:00.000Z"),
		updatedAt: new Date("2025-01-10T09:00:00.000Z"),
	},
	{
		id: "00000000-0000-4000-8000-000000000002",
		partNumber: "ART-002",
		partName: "Blech",
		siteId: "00000000-0000-4000-8000-000000000010",
		imageBlobPath: null,
		active: true,
		createdAt: new Date("2025-02-12T11:30:00.000Z"),
		updatedAt: new Date("2025-02-12T11:30:00.000Z"),
	},
	{
		id: "00000000-0000-4000-8000-000000000003",
		partNumber: "ART-003",
		partName: "Flansch",
		siteId: "00000000-0000-4000-8000-000000000010",
		imageBlobPath: "part/11111111-0000-4000-8000-000000000003",
		active: true,
		createdAt: new Date("2025-03-15T14:15:00.000Z"),
		updatedAt: new Date("2025-03-15T14:15:00.000Z"),
	},
	{
		id: "00000000-0000-4000-8000-000000000004",
		partNumber: "ART-004",
		partName: "Getriebe",
		siteId: "00000000-0000-4000-8000-000000000010",
		imageBlobPath: null,
		active: true,
		createdAt: new Date("2025-04-20T08:45:00.000Z"),
		updatedAt: new Date("2025-04-20T08:45:00.000Z"),
	},
	{
		id: "00000000-0000-4000-8000-000000000005",
		partNumber: "ART-005",
		partName: "Stecker",
		siteId: "00000000-0000-4000-8000-000000000011",
		imageBlobPath: null,
		active: true,
		createdAt: new Date("2025-05-05T10:00:00.000Z"),
		updatedAt: new Date("2025-05-05T10:00:00.000Z"),
	},
	{
		id: "00000000-0000-4000-8000-000000000006",
		partNumber: "ART-006",
		partName: "Schiene",
		siteId: "00000000-0000-4000-8000-000000000011",
		imageBlobPath: null,
		active: true,
		createdAt: new Date("2025-05-18T13:20:00.000Z"),
		updatedAt: new Date("2025-05-18T13:20:00.000Z"),
	},
	{
		id: "00000000-0000-4000-8000-000000000007",
		partNumber: "ART-007",
		partName: "Deckel",
		siteId: "00000000-0000-4000-8000-000000000010",
		imageBlobPath: null,
		active: true,
		createdAt: new Date("2025-06-01T09:30:00.000Z"),
		updatedAt: new Date("2025-06-01T09:30:00.000Z"),
	},
	{
		id: "00000000-0000-4000-8000-000000000008",
		partNumber: "ART-008",
		partName: "Welle",
		siteId: "00000000-0000-4000-8000-000000000011",
		imageBlobPath: null,
		active: false,
		createdAt: new Date("2025-06-10T15:45:00.000Z"),
		updatedAt: new Date("2025-06-10T15:45:00.000Z"),
	},
];

/**
 * In-memory implementation of {@link PartRepository} for demo/offline mode.
 *
 * Seeds itself with {@link DEFAULT_PARTS} unless an explicit seed is passed to
 * the constructor. Swappable in place of `CloudPartRepository` via the
 * `provideDemoPartRepository` helper.
 */

export class DemoPartRepository implements PartRepository {
	private parts: Part[];

	constructor(seed: Part[] = DEFAULT_PARTS) {
		// Store a defensive copy so callers cannot mutate the seed array.
		this.parts = seed.map((p) => ({ ...p }));
	}

	getAll(siteId?: string): Observable<Part[]> {
		const filtered = siteId ? this.parts.filter((p) => p.siteId === siteId) : this.parts.slice();
		return of(filtered);
	}

	getById(id: string): Observable<Part | null> {
		return of(this.parts.find((p) => p.id === id) ?? null);
	}

	getByPartNumber(partNumber: string): Observable<Part | null> {
		return of(this.parts.find((p) => p.partNumber === partNumber) ?? null);
	}

	create(input: CreatePartInput): Observable<Part> {
		const now = new Date();
		const part: Part = {
			id: crypto.randomUUID(),
			partNumber: input.partNumber,
			partName: input.partName,
			siteId: input.siteId ?? null,
			imageBlobPath: input.imageBlobPath ?? null,
			active: input.active ?? true,
			createdAt: now,
			updatedAt: now,
		};
		this.parts = [...this.parts, part];
		return of(part);
	}

	update(id: string, changes: Partial<Part>): Observable<Part> {
		const index = this.parts.findIndex((p) => p.id === id);
		if (index < 0) {
			return throwError(() => new Error(`Part #${id} not found`));
		}
		const updated: Part = { ...this.parts[index], ...changes, updatedAt: new Date() };
		this.parts = this.parts.map((p, i) => (i === index ? updated : p));
		return of(updated);
	}

	delete(id: string): Observable<void> {
		const exists = this.parts.some((p) => p.id === id);
		if (!exists) {
			return throwError(() => new Error(`Part #${id} not found`));
		}
		this.parts = this.parts.filter((p) => p.id !== id);
		return of(undefined);
	}
}
